"use client";

import { flattenCategories } from "@/lib/helpers/categoryHelpers";
import { CardsList } from "@/lib/interfaces/cards";
import { CategoryList } from "@/lib/interfaces/categories";
import { CardService } from "@/lib/services/cardService";
import { CategoryService } from "@/lib/services/categoryService";
import Image from "next/image";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { ArrowDown, Loader2, X } from "lucide-react";

interface BottomSheetProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    categorySlug: string;
    onSelect: (card: CardsList) => void;
    isCategoryUUID?: boolean;
    categoryUUID?: string;
}

/**
 * Design picker. Mobile: a bottom sheet (drag down to close). Desktop: a panel
 * that slides in from the left over 60% of the screen, 5 designs per row.
 * Designs load 20 at a time with "Load more".
 */
const CoverSheet: React.FC<BottomSheetProps> = ({
    isOpen,
    onClose,
    title,
    categorySlug,
    onSelect,
    isCategoryUUID = false,
    categoryUUID
}) => {
    const [selectedCategory, setSelectedCategory] = useState<string | null>(isCategoryUUID ? null : categorySlug);
    const [categories, setCategories] = useState<CategoryList[]>([]);
    const [cards, setCards] = useState<CardsList[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const panelRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const loadedRef = useRef(false);
    const touchStartY = useRef(0);
    const touchCurrentY = useRef(0);

    /** Load one page of designs; page 1 replaces the list, later pages append. */
    const loadCards = useCallback(async (slug: string, pageToLoad: number) => {
        setIsLoading(true);
        try {
            const res = await CardService.getCardsBySlug(slug, {
                filters: '',
                is_latest: true,
                is_staff_pick: false,
                sort_by: 'position',
                sort_order: 'asc',
                page: pageToLoad,
            });
            if (res?.response_status && res?.response_data) {
                const batch = res.response_data as CardsList[];
                setCards((prev) => (pageToLoad === 1 ? batch : [...prev, ...batch]));
                setPage(pageToLoad);
                setHasMore(!!res.pagination?.next && batch.length > 0);
                setTotal(typeof res.pagination?.count === 'number' ? res.pagination.count : null);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error('Error loading designs:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Resolve the category (by UUID if needed) and load the first page — only
    // once the picker is first opened, so pages that never open it fetch nothing.
    useEffect(() => {
        if (!isOpen || loadedRef.current) return;
        loadedRef.current = true;
        if (isCategoryUUID && categoryUUID) {
            CategoryService.getCategoryDetail(categoryUUID)
                .then((res) => {
                    if (res.response_status && res.response_data) {
                        const slug = (res.response_data as CategoryList).category_slug;
                        setSelectedCategory(slug);
                        loadCards(slug, 1);
                    }
                })
                .catch((err) => console.error('Error fetching category by UUID:', err));
        } else if (categorySlug) {
            loadCards(categorySlug, 1);
        }
    }, [isOpen, isCategoryUUID, categoryUUID, categorySlug, loadCards]);

    // Category chips (only when the caller didn't pin a category).
    useEffect(() => {
        if (!isOpen || categories.length > 0 || (isCategoryUUID && categoryUUID)) return;
        CategoryService.getCategories()
            .then((result) => setCategories(result.response_status && result.response_data ? flattenCategories(result.response_data) : []))
            .catch(() => setCategories([]));
    }, [isOpen, categories.length, isCategoryUUID, categoryUUID]);

    // Escape closes; lock page scroll while open.
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCloseRef.current();
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKey);
        panelRef.current?.focus();
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('keydown', onKey);
        };
    }, [isOpen]);

    const selectCategory = (slug: string) => {
        if (slug === selectedCategory) return;
        setSelectedCategory(slug);
        setCards([]);
        loadCards(slug, 1);
    };

    // Drag-down-to-close (mobile bottom sheet only).
    const onTouchStart = (e: React.TouchEvent) => {
        touchStartY.current = e.touches[0].clientY;
        touchCurrentY.current = touchStartY.current;
    };
    const onTouchMove = (e: React.TouchEvent) => {
        touchCurrentY.current = e.touches[0].clientY;
        const deltaY = touchCurrentY.current - touchStartY.current;
        if (deltaY > 0 && panelRef.current && window.innerWidth < 1024) {
            panelRef.current.style.transform = `translateY(${deltaY}px)`;
        }
    };
    const onTouchEnd = () => {
        if (!panelRef.current) return;
        if (touchCurrentY.current - touchStartY.current > 100 && window.innerWidth < 1024) onClose();
        panelRef.current.style.transform = '';
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-ink/40 lg:bg-ink/10" onClick={onClose} aria-hidden="true" />

            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="cover-sheet-title"
                tabIndex={-1}
                className="cover-sheet absolute inset-x-0 bottom-0 flex max-h-[85vh] min-h-[50vh] flex-col rounded-t-3xl bg-white shadow-2xl outline-none lg:inset-y-0 lg:left-0 lg:right-auto lg:max-h-none lg:w-[60vw] lg:rounded-none lg:rounded-r-3xl"
            >
                {/* Header (drag handle on mobile) */}
                <div className="shrink-0 border-b border-paper-rule" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
                    <div className="mx-auto mt-2 h-1 w-12 rounded-full bg-paper-rule lg:hidden" aria-hidden="true" />
                    <div className="flex items-center justify-between gap-3 px-5 py-3 lg:px-8 lg:py-5">
                        <div>
                            <h2 id="cover-sheet-title" className="text-xl tracking-tight text-ink lg:text-2xl">{title}</h2>
                            <p className="text-xs text-gray-600 lg:text-sm">
                                {total ? `${cards.length} of ${total.toLocaleString('en-US')} designs` : 'Pick a design for your card'}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className="flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-paper"
                        >
                            <X className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </div>

                    {!isCategoryUUID && categories.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto px-5 pb-3 [scrollbar-width:none] lg:px-8 [&::-webkit-scrollbar]:hidden">
                            {categories.map((category) => (
                                <button
                                    type="button"
                                    key={category.id}
                                    onClick={() => selectCategory(category.category_slug)}
                                    className={`shrink-0 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${selectedCategory === category.category_slug
                                        ? 'bg-ink text-white'
                                        : 'border border-paper-rule bg-paper text-ink hover:bg-paper-linen'
                                        }`}
                                >
                                    {category.category_name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Designs */}
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 lg:px-8 lg:py-6">
                    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 lg:gap-4">
                        {cards.map((card) => (
                            <li key={card.id}>
                                <button
                                    type="button"
                                    onClick={() => onSelect(card)}
                                    className="group block w-full rounded-2xl border border-paper-rule bg-white p-1.5 text-left transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_28px_-16px_rgba(6,35,63,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink"
                                >
                                    <span className="relative block aspect-[4/5] overflow-hidden rounded-xl bg-paper">
                                        <Image
                                            src={card.card_image}
                                            alt=""
                                            fill
                                            sizes="(min-width: 1024px) 11vw, (min-width: 640px) 30vw, 45vw"
                                            className="object-cover"
                                        />
                                    </span>
                                    <span className="line-clamp-1 px-1 pb-0.5 pt-2 text-xs font-medium text-ink">{card.name}</span>
                                </button>
                            </li>
                        ))}
                        {isLoading && Array.from({ length: cards.length === 0 ? 10 : 5 }).map((_, i) => (
                            <li key={`s${i}`} className="aspect-[4/5] animate-pulse rounded-2xl bg-paper-linen" aria-hidden="true" />
                        ))}
                    </ul>

                    {!isLoading && cards.length === 0 && (
                        <p className="py-10 text-center text-sm text-gray-600">No designs found in this category.</p>
                    )}

                    {hasMore && selectedCategory && (
                        <div className="mt-6 flex justify-center pb-2">
                            <button
                                type="button"
                                onClick={() => loadCards(selectedCategory, page + 1)}
                                disabled={isLoading}
                                className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition hover:bg-ink-700 disabled:opacity-60"
                            >
                                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ArrowDown className="h-4 w-4" aria-hidden="true" />}
                                {isLoading ? 'Loading designs…' : 'Load more designs'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CoverSheet
